import React, { useEffect, useRef, useState } from "react";
import DashboardTopBarAdmin from "../../components/admin/DashboardTopBarAdmin";
import PrimaryButton from "../../components/shared/PrimaryButton";
import ErrorButton from "../../components/shared/ErrorButton";
import Modal from "../../components/shared/Modal";
import "./ProductsAdmin.css";
import productsBlueIcon from "../../assets/admin/icons/products-blue.svg";
import plusIcon from "../../assets/admin/icons/plus-white.svg";
import ProductFormAdmin from "../../components/admin/products/ProductFormAdmin";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import ProductCard from "../../components/admin/products/ProductCardAdmin";
import { fetchProducts, deleteProduct } from "../../util/admin/httpAdmin";
import LoadingIndicatorButton from "../../components/shared/LoadingIndicatorButton";

import { useNavigate, useLocation } from "react-router-dom";

function ProductsAdmin() {
   const modalRef = useRef();
   const deleteModalRef = useRef();
   const [selectedProduct, setSelectedProduct] = useState(null);
   const [productToDelete, setProductToDelete] = useState(null);
   const [searchTerm, setSearchTerm] = useState("");
   const [filterCollection, setFilterCollection] = useState("");

   const queryClient = useQueryClient();
   const location = useLocation();
   const navigate = useNavigate();

   const openModal = () => modalRef.current.open();
   const closeModal = () => {
      if(modalRef.current) {
         modalRef.current.close();
      }

      setTimeout(() => {
         setSelectedProduct(null); // Reset geselecteerd product
      }, 500)
   };

   const beforeCloseAddForm = () => {
      const params = new URLSearchParams(location.search);
      params.delete("add");

      const newQuery = params.toString();
      navigate(`${location.pathname}${newQuery ? `?${newQuery}` : ""}`, {
         replace: true,
      });
   };

   useEffect(() => {
      const params = new URLSearchParams(location.search);
      if (params.get("add") === "true") {
         openModal();
      }
   }, [location]);

   const openDeleteModal = (product) => {
      setProductToDelete(product);
      deleteModalRef.current.open();
   };

   const closeDeleteModal = () => {
      deleteModalRef.current.close();
      setProductToDelete(null);
   };

   const handleEditButtonClick = (product) => {
      setSelectedProduct(product);
      openModal();
   };

   const {
      data: products,
      isPending,
      isError,
      error,
   } = useQuery({
      queryKey: ["adminProducts"],
      queryFn: ({ signal }) => fetchProducts({ signal }),
   });

   const deleteMutation = useMutation({
      mutationFn: deleteProduct,
      onSuccess: () => {
         queryClient.invalidateQueries(["adminProducts"]);
         queryClient.refetchQueries(["adminProducts"]);
         closeDeleteModal();
      },
      onError: (error) => {
         console.error("Fout bij het verwijderen van product:", error);
      },
   });

   // Process collections for dropdown filter
   const getUniqueCollections = () => {
      if (!products || products.length === 0) return [];
      
      const collections = products
         .filter(product => product.collection)
         .map(product => ({
            id: product.collection._id,
            name: product.collection.name
         }));
      
      // Remove duplicates
      return [...new Map(collections.map(item => [item.id, item])).values()];
   };

   const filteredProducts = () => {
      if (!products) return [];
      
      return products.filter(product => {
         const matchesSearch = searchTerm === "" || 
            product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()));
         
         const matchesCollection = filterCollection === "" ||
            (product.collection && product.collection._id === filterCollection);
         
         return matchesSearch && matchesCollection;
      });
   };

   const uniqueCollections = getUniqueCollections();

   return (
      <div>
         <DashboardTopBarAdmin
            title="Producten"
            icon={productsBlueIcon}
            description="Hier kan je al jouw producten bekijken en bewerken"
         >
            <PrimaryButton
               onClick={() => {
                  setSelectedProduct(null);
                  openModal();
               }}
            >
               <div className="g-15-1fr">
                  <img src={plusIcon} alt="add" />{" "}
                  <span>Product Toevoegen</span>
               </div>
            </PrimaryButton>
         </DashboardTopBarAdmin>

         <div className="products-container py-20 px-20">
            <div className="products-header">
               <div className="search-container">
                  <input
                     type="text"
                     placeholder="Zoek producten..."
                     value={searchTerm}
                     onChange={(e) => setSearchTerm(e.target.value)}
                     className="search-input"
                  />
               </div>
               
               <div className="filter-container">
                  <label className="filter-label">Filter op collectie:</label>
                  <select
                     className="filter-select"
                     value={filterCollection}
                     onChange={(e) => setFilterCollection(e.target.value)}
                  >
                     <option value="">Alle collecties</option>
                     {uniqueCollections.map(collection => (
                        <option key={collection.id} value={collection.id}>
                           {collection.name}
                        </option>
                     ))}
                  </select>
               </div>
            </div>

            <div className="products-content">
               {isPending && (
                  <div className="loading-indicator">
                     <LoadingIndicatorButton dark />
                  </div>
               )}
               
               {isError && (
                  <div className="error-message">
                     <p className="error-text">
                        {error.info?.message || "Er is iets foutgegaan bij het ophalen van de producten"}
                     </p>
                  </div>
               )}
               
               {products && filteredProducts().length === 0 && !isPending && (
                  <div className="empty-state">
                     {searchTerm || filterCollection ? (
                        <p>Geen producten gevonden met de huidige filters.</p>
                     ) : (
                        <p>Je hebt nog geen producten aangemaakt.</p>
                     )}
                  </div>
               )}
               
               {products && filteredProducts().length > 0 && !isError && !isPending && (
                  <div className="products-grid">
                     {filteredProducts().map((product) => (
                        <ProductCard
                           key={product._id}
                           product={product}
                           onEditClick={() => handleEditButtonClick(product)}
                           onDeleteClick={() => openDeleteModal(product)}
                        />
                     ))}
                  </div>
               )}
            </div>
         </div>

         {/* Modal voor bewerken/toevoegen */}
         <Modal ref={modalRef} beforeClose={beforeCloseAddForm}>
            <div className="modal-title-container">
               <h3 className="modal-title">
                  {selectedProduct ? "Product bewerken" : "Product toevoegen"}
               </h3>
            </div>
            <ProductFormAdmin
               onSuccessClose={closeModal}
               editMode={!!selectedProduct}
               previousData={selectedProduct || {}}
            />
         </Modal>

         {/* Modal voor verwijderen */}
         <Modal ref={deleteModalRef}>
            <div className="g-20">
               <h3>Product verwijderen</h3>
               {productToDelete && (
                  <>
                     <p>
                        Weet je zeker dat je het product "{productToDelete.name}" wilt
                        verwijderen?
                     </p>
                     <p className="c-r f-s">
                        Deze actie kan niet ongedaan worden gemaakt.
                     </p>
                     <div className="flex-1 g-10">
                        <ErrorButton
                           onClick={() => deleteMutation.mutate(productToDelete._id)}
                           disabled={deleteMutation.isPending}
                        >
                           {deleteMutation.isPending ? (
                              <LoadingIndicatorButton />
                           ) : (
                              "Verwijderen"
                           )}
                        </ErrorButton>
                        <PrimaryButton onClick={closeDeleteModal}>
                           Annuleren
                        </PrimaryButton>
                     </div>
                  </>
               )}
            </div>
         </Modal>
      </div>
   );
}

export default ProductsAdmin;
